import React, { useEffect, useState, useContext } from 'react';
import { green, blue } from '@material-ui/core/colors';
import {
  useMediaQuery,
  CircularProgress,
  makeStyles,
  Typography
} from '@material-ui/core';

import BarChart, { BarChartTypes } from '../../Components/Graph/BarChart';
import SweetAlert from '../../Components/Alerts/SweetAlert';

import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';
import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { GetIndustrialContractVolumesAPI } from '../../API/Industrials/IndustrialsAPI';

import {
  extractErrorMessage,
  redirectOnAuthFailure
} from '../../Utils/Errors/Errors';
import _get from 'lodash/get';
import { logoutUser } from '../../Utils/User/Actions';
import { HeliosConcepts } from '../../Enums/concepts';

const parseConsumptionData = volumesData => {
  //We don't need to display commerzialization data as it has the same values as supply
  const conceptList = Object.keys(volumesData.concepts).filter(
    concept => concept !== HeliosConcepts.commercialization
  );
  const dates = volumesData.concepts[conceptList[0]].details.map(
    element => element.day
  );

  const concepts = conceptList.map(concept => ({
    label: volumesData.concepts[concept].unit,
    values: volumesData.concepts[concept].details.map(element => element.volume)
  }));

  return { dates, concepts };
};

const IndustrialInvoiceConsumptionGraph = props => {
  const { contractId, year, month } = props;

  const classes = useStyles();

  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);

  const setAlert = useContext(AlertsDispatchContext);

  const [loading, setLoading] = useState(true);
  const [volumesData, setVolumesData] = useState({
    dates: [],
    concepts: [{ label: '', unit: '', values: [] }]
  });
  const authToken = _get(currentUser, 'token');

  const smallScreen = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  useEffect(() => {
    let ignoreRequest = false;

    const fechData = async () => {
      setLoading(true);

      const response = await GetIndustrialContractVolumesAPI(
        contractId,
        authToken,
        month,
        year
      );

      if (ignoreRequest) {
        return;
      }

      if (!response.success) {
        setLoading(false);

        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }

        setAlert({
          type: 'error',
          message: extractErrorMessage(response).message
        });
        return;
      }

      const consumptionData = response.data;

      if (consumptionData.length === 0) {
        setLoading(false);
        setAlert({
          type: 'error',
          message:
            'No se encontraron datos de consumo para el último mes facturado.'
        });
        return;
      }

      const parsedConsumptionData = parseConsumptionData(consumptionData[0]);

      setVolumesData(parsedConsumptionData);
      setLoading(false);
    };

    fechData();

    return () => {
      ignoreRequest = true;
    };
  }, [authToken, setAlert, setCurrentUser, contractId, month, year]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={32} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <SweetAlert
        type="info"
        message={
          <Typography component="span" className={classes.fontSizeAlert}>
            Puedes visualizar el consumo según la métrica seleccionando la
            leyenda del concepto correspondiente.
          </Typography>
        }
      />
      <BarChart
        title={`Periodo ${year}-${month}`}
        features={volumesData.dates.map(date => date.split('-')[2])}
        defaultDataset={0}
        type={BarChartTypes.Industrial}
        data={volumesData.concepts.map((volumeConcept, index) => ({
          id: index,
          index,
          label: volumeConcept.label,
          data: volumeConcept.values,
          borderColor: index % 2 === 0 ? blue.A700 : green.A700,
          backgroundColor: index % 2 === 0 ? blue.A100 : green.A100,
          borderWidth: 2,
          borderSkipped: false,
          buttonStyle: index % 2 === 0 ? classes.m3Button : classes.kwhButton,
          hidden: index !== 0
        }))}
        height={smallScreen ? 400 : undefined}
      />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 20
  },
  loadingContainer: {
    width: '100%',
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  m3Button: {
    borderColor: blue[100],
    backgroundColor: blue[100],
    '&:hover': {
      transition: 'all 1.2s ease',
      borderColor: `${blue.A700} !important`,
      backgroundColor: `${blue.A700} !important`
    },
    '&:disabled': {
      color: theme.palette.common.white,
      borderColor: blue.A700,
      backgroundColor: blue.A700
    }
  },
  kwhButton: {
    borderColor: green[100],
    backgroundColor: green[100],
    '&:hover': {
      borderColor: `${green.A700} !important`,
      backgroundColor: `${green.A700} !important`,
      transition: 'all 1.2s ease'
    },
    '&:disabled': {
      color: theme.palette.common.white,
      borderColor: green.A700,
      backgroundColor: green.A700
    }
  },
  fontSizeAlert: {
    fontSize: 13
  },
  paddingMeter: {
    paddingTop: 5
  }
}));

export default IndustrialInvoiceConsumptionGraph;
